import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, token } from '@bp-files/ui';
import { UPLOAD_COPY } from '@/components/UploadPanel/uploadPanel.copy';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';

/**
 * The page is the drop target (FIL-7, **ADR-0061**).
 *
 * A drag anywhere over an open folder raises one full-viewport overlay, and a
 * drop anywhere hands the files to the queue. There is no zone to find, because
 * a person already holding files from their file manager cannot go looking for
 * one.
 *
 * **It listens to `dragover` and `drop`. Not `dragenter`, not `dragleave`, not
 * `dragend`** — and the omission is the design rather than an oversight, which
 * is why ADR-0061 exists. Every classic hazard here belongs to the two events
 * that are missing: `dragleave` firing when the pointer crosses a child
 * boundary rather than leaving the region, an overlay appearing under the
 * cursor and churning its own enter/leave, `relatedTarget === null`
 * browser-sniffing, counters that desync by one and strand the overlay until
 * reload. `dragover` bubbles to `window` from whatever element is beneath the
 * cursor, so one listener sees a continuous heartbeat with nothing to pair and
 * nothing to count.
 *
 * `dragend` is not available at all: it fires on the drag **source**, which for
 * an OS file drag is the file manager rather than this document.
 *
 * **It absorbs what `useStrayDropGuard` used to do**, and both handlers
 * `preventDefault()` above every gate. The browser's default is to navigate the
 * tab to `file:///…`, which unmounts everything and kills every transfer in
 * flight — the same data loss as closing the tab, reached by an easier
 * accident. That has to hold for a suppressed viewer, a read-only folder, and a
 * dragged hyperlink alike, so **`mode: 'off'` never means unsubscribing**: it
 * means the overlay does not paint and the files are discarded.
 *
 * **It is a fourth overlay and it borrows rather than extends.** `role="status"`
 * and no focus trap: there is nothing to focus, no control, no escape route,
 * and no focus to restore — a drag is a mouse-only gesture already in progress.
 * It takes `ViewerFrame`'s `z-50` and `ConsentDialog`'s scrim, and shares the
 * rung safely because the two are mutually exclusive by construction (`off` is
 * exactly the viewer being open).
 *
 * **Suppression is the caller's fact, not a DOM probe.** Asking
 * `document.querySelector('[role="dialog"][aria-modal="true"]')` at drag time
 * was considered — it cannot be forgotten when a fourth modal lands — and
 * rejected: it couples this to other components' markup and would silently
 * begin suppressing on any future non-modal dialog.
 */

/**
 * How long after the last `dragover` the overlay gives up.
 *
 * **Derived, not chosen.** The HTML drag-and-drop processing model iterates
 * every 350ms ±200ms, so the largest sanctioned gap between two `dragover`
 * events on a *stationary* drag is 550ms. Anything at or below that clears the
 * overlay while the reader is holding still over the page — the exact moment
 * they are reading it. This is the smallest round number with headroom, and
 * tightening it will look safe and will not be.
 *
 * The cost is that a drag leaving the window without dropping leaves the
 * overlay up for up to this long. The accelerator — a `dragleave` clearing on
 * coordinates outside the viewport — is a second mechanism for one question and
 * brings the whole hazard class back with it (ADR-0061).
 */
export const DRAG_IDLE_MS = 700;

/**
 * `accepting` takes the drop; `readOnly` answers the gesture and discards it;
 * `off` paints nothing and discards it. All three still swallow the event.
 *
 * One discriminated value rather than two booleans, matching `state.kind` and
 * `phase.kind` elsewhere in this app: three exhaustive states with no invalid
 * combination between them.
 */
export type DropMode = 'accepting' | 'readOnly' | 'off';

export interface UploadDropTargetProps {
  mode: DropMode;
  /**
   * Undefined until the supplementary space call settles, and after it fails.
   * The line is omitted rather than filled with `unnamedFolder` ("Name
   * unavailable"), which would read as a broken sentence.
   *
   * Required and nullable rather than optional, on this workspace's own
   * pattern: a caller that does not know the name should have to say so.
   */
  folderName: string | undefined;
  /**
   * Called on a `mode: 'accepting'` drop, with the files and the names of any
   * directories that came with them. Directories are separated here because
   * this is the only thing holding a `DataTransfer`; everything downstream
   * stays in `File[]` currency, which is what keeps the drop and the OS picker
   * genuinely the same path.
   */
  onFiles: (files: readonly File[], directoryNames: readonly string[]) => void;
}

/** The gate. A text or space drag is swallowed without being announced. */
function carriesFiles(transfer: DataTransfer | null): boolean {
  return Array.from(transfer?.types ?? []).includes('Files');
}

/**
 * Both the `File` and the is-it-a-directory answer come from `items`, rather
 * than pairing `items[i]` against `files[i]` by index — the two lists are
 * parallel in practice and nothing guarantees the alignment.
 *
 * Must run synchronously inside the handler: the `items` list is neutered once
 * the event returns.
 */
function partition(transfer: DataTransfer | null): {
  files: File[];
  directoryNames: string[];
} {
  const items = [...(transfer?.items ?? [])].filter((item) => item.kind === 'file');

  // No `items` at all — `webkitGetAsEntry` is non-standard, and where it is
  // missing a directory is indistinguishable from an empty file and is refused
  // as one, exactly as it was before this component existed.
  if (items.length === 0) return { files: [...(transfer?.files ?? [])], directoryNames: [] };

  const files: File[] = [];
  const directoryNames: string[] = [];

  for (const item of items) {
    const file = item.getAsFile();
    if (file === null) continue;

    if (item.webkitGetAsEntry()?.isDirectory === true) directoryNames.push(file.name);
    else files.push(file);
  }

  return { files, directoryNames };
}

export function UploadDropTarget({ mode, folderName, onFiles }: UploadDropTargetProps) {
  const [over, setOver] = useState(false);

  /*
    Read at call time rather than captured, on `useUploadQueue`'s own pattern.
    The listeners subscribe once and never re-subscribe: a drag can begin in the
    same tick a viewer closes, and re-attaching window listeners on every mode
    change is churn for no benefit.

    Synced in an effect rather than during render — a render is not allowed to
    touch a ref — and **declared before the subscription below**, because
    effects fire in declaration order and the listeners read what this writes.
  */
  const latest = useRef({ mode, onFiles });
  useEffect(() => {
    latest.current = { mode, onFiles };
  });

  useEffect(() => {
    let idle: ReturnType<typeof setTimeout> | undefined;

    const stop = () => {
      if (idle !== undefined) clearTimeout(idle);
      idle = undefined;
    };

    const onDragOver = (event: DragEvent) => {
      // Above the gates, unconditionally: this is the stray-drop guard, and it
      // has to hold for every mode. (If an editable field ever lands on this
      // surface, the exemption goes here — there is none today.)
      event.preventDefault();

      if (!carriesFiles(event.dataTransfer)) return;
      if (latest.current.mode === 'off') return;

      // Only on the accepting path. `'none'` would give the read-only branch
      // the right cursor and also suppress the `drop` event per spec, handing
      // the browser control of the one path whose whole point is that we
      // swallow the gesture ourselves.
      if (latest.current.mode === 'accepting' && event.dataTransfer !== null) {
        event.dataTransfer.dropEffect = 'copy';
      }

      setOver(true);
      stop();
      idle = setTimeout(() => {
        setOver(false);
      }, DRAG_IDLE_MS);
    };

    const onDrop = (event: DragEvent) => {
      event.preventDefault();
      stop();
      setOver(false);

      if (latest.current.mode !== 'accepting') return;
      if (!carriesFiles(event.dataTransfer)) return;

      const { files, directoryNames } = partition(event.dataTransfer);
      latest.current.onFiles(files, directoryNames);
    };

    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      stop();
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  if (!over || mode === 'off') return null;

  const readOnly = mode === 'readOnly';
  const headline = readOnly ? UPLOAD_COPY.drop.readOnly : UPLOAD_COPY.drop.active;
  const detail = readOnly
    ? FOLDER_COPY.states.readOnly
    : folderName === undefined
      ? undefined
      : UPLOAD_COPY.drop.activeIn(folderName);

  /*
    Portalled, and that is load-bearing rather than stylistic: `ViewerFrame`
    records why. `bp-animate-fade-in` on an ancestor creates a stacking context,
    so a `z-50` inside one is not 50 — and this renders under `AppShell`'s
    `z-20` sticky header.

    `pointer-events-none` throughout. The drop is handled on `window`, so the
    overlay never needs to be the target — and if the state machine ever
    stranded it, a non-interactive layer is a cosmetic bug rather than a
    page-wide input lock.
  */
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-6">
      <div
        className="absolute inset-0 backdrop-blur-xl"
        style={{ background: token('bp-scrim') }}
      />

      <div
        role="status"
        aria-label={headline}
        className="bp-animate-fade-in relative z-10 flex flex-col items-center gap-2 border border-dashed px-8 py-6 text-center"
        style={{
          background: token('bp-card'),
          // The one idiom worth carrying over from the dashed zone this
          // replaced: it is already what "release here" looks like here.
          borderColor: readOnly ? token('bp-border-strong') : token('bp-blue'),
          boxShadow: token('shadow-xl'),
          borderRadius: token('radius-2xl'),
          // `fast`, not `normal`. A modal earns 250ms because it interrupts;
          // this has to land before the reader's hand does.
          animationDuration: motion.fast,
        }}
      >
        <p className="text-h2 font-semibold" style={{ color: token('bp-text') }}>
          {headline}
        </p>

        {detail === undefined ? null : (
          <p className="text-sm" style={{ color: token('bp-text-secondary') }}>
            {detail}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
